import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";

// Texas Scramble team allowance: 25% of lowest HCP, 20% of 2nd lowest, 15% of 3rd, 10% of highest

// CONGU/WHS scramble allowance table varies by team size — was hardcoded
// for 4 players only. Now adapts to the group's actual member count, so an
// odd-sized group (e.g. a 3-ball among mostly 4-balls) still gets the
// correct table automatically regardless of which scramble format was
// selected at event setup.
function getScramblePcts(count) {
  if (count === 3) return [0.30, 0.20, 0.10];
  if (count === 2) return [0.35, 0.15];
  return [0.25, 0.20, 0.15, 0.10];
}
const calcScrambleAllowance = (members) => {
  const pcts = getScramblePcts(members.length);
  const sorted = [...members].sort((a, b) => ((a.hcp_adj ?? a.handicap) || 0) - ((b.hcp_adj ?? b.handicap) || 0));
  return Math.round(sorted.reduce((sum, p, i) => sum + ((p.hcp_adj ?? p.handicap) || 0) * (pcts[i] || 0), 0));
};

export default function TeamResults() {
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [archiveRows, setArchiveRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [toast, setToast] = useState("");
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 4000); };
  const prizesRef = React.useRef({ nearest_pin: "", nearest_pin2: "", longest_drive: "" });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      const [scoresRes, teamsRes, holesRes, prizesRes] = await Promise.all([
        api.get("/scores/" + eventRes.data.id),
        api.get("/teams"),
        api.get("/courses/" + eventRes.data.course_id + "/holes"),
        api.get("/events/prizes").catch(() => ({ data: { nearest_pin: "", nearest_pin2: "", longest_drive: "" } }))
      ]);

      const players = (teamsRes.data.players || []).filter(p => p.playing);
      const holes = holesRes.data;
      const scores = scoresRes.data;
      const isMedal = eventRes.data.game_type?.includes("Medal") || eventRes.data.game_type?.includes("Stroke");
      const isAggregate = eventRes.data.game_type?.startsWith("Team Aggregate");
      const isScramble = eventRes.data.game_type?.startsWith("Texas Scramble");

      const teamResults = [];
      const archiveList = [];

      if (isScramble) {
        // Texas Scramble: each physical playing group is its own scramble team —
        // matches how TeamAggregateScoreEntry.jsx saves scores (one team per grp),
        // not the 2-named-team system used for Team Stableford/Medal below.
        // Everyone plays one shared ball, so — same convention as Foursomes —
        // every member is archived with the team's one shared result.
        const grpMap = {};
        players.forEach(p => {
          if (!grpMap[p.grp]) grpMap[p.grp] = [];
          grpMap[p.grp].push(p);
        });

        Object.entries(grpMap).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).forEach(([grp, members]) => {
          const combHcp = members.reduce((s, p) => s + ((p.hcp_adj ?? p.handicap) || 0), 0);
          const allowance = calcScrambleAllowance(members);
          const leadPlayer = members[0];
          const teamScores = scores.filter(s => s.player_id === leadPlayer.player_id);
          let total = 0, thru = 0, gross = 0;
          const holeGross = [];
          holes.forEach(hole => {
            const s = teamScores.find(s => s.hole === hole.hole);
            if (s) {
              const g = parseInt(s.gross);
              gross += g;
              const base = Math.floor(allowance / 18);
              const extra = allowance % 18;
              const shots = base + (hole.stroke_index <= extra ? 1 : 0);
              const net = g - shots;
              // Texas Scramble traditionally scores by net strokes, not
              // points -- "Texas Scramble Medal" formats (added
              // 2026-09-09) score that way; the original points-based
              // "Texas Scramble" formats are unchanged.
              total += isMedal ? net : Math.max(0, 2 + (hole.par - net));
              thru++;
              holeGross.push(g);
            } else { holeGross.push(0); }
          });
          teamResults.push({
            teamId: "grp" + grp,
            name: "Group " + grp,
            members: members.map(p => p.name.split(" ")[0]).join(", "),
            total: thru > 0 ? total : "-",
            thru
          });
          const net = gross - allowance;
          members.forEach(p => {
            archiveList.push({ player_id: p.player_id, name: p.name, handicap: p.handicap, teamKey: "grp" + grp, team_name: "Group " + grp, gross: gross || 0, net: gross ? net : 0, points: total || 0, holeGross: [...holeGross] });
          });
        });
      } else {
        // Team Stableford/Medal/Aggregate: genuine 2-rival-team battle, grouped
        // by the named-team system. The TEAM result (win/loss/position) is a
        // separate achievement from each player's own round — a player's
        // permanent record should only ever reflect what they personally
        // scored, never a teammate's hole win, so Player Stats stay honest.
        // Confirmed with Mike 2026-09-01.
        const teams = teamsRes.data.teams || [];
        const teamMap = {};
        teams.forEach(t => { teamMap[t.id] = { name: t.team_name, members: [] }; });

        players.forEach(p => {
          if (p.team_id && teamMap[p.team_id]) {
            teamMap[p.team_id].members.push(p);
          }
        });

        const ghostId = eventRes.data.ghost_player_id;
        const ghostPlayer = ghostId ? players.find(p => p.player_id === ghostId) : null;
        if (ghostPlayer) {
          Object.values(teamMap).forEach(team => {
            if (team.members.length === 3 && !team.members.some(m => m.player_id === ghostId)) {
              team.members.push({ player_id: ghostPlayer.player_id, name: "Ghost (" + ghostPlayer.name + ")", handicap: ghostPlayer.handicap, hcp_adj: ghostPlayer.hcp_adj ?? ghostPlayer.handicap, isGhost: true });
            }
          });
        }

        Object.entries(teamMap).forEach(([teamId, team]) => {
          const members = team.members;
          if (members.length === 0) return;

          let total = 0, thru = 0;
          members.forEach(p => {
            if (p.isGhost) return;
            archiveList.push({ player_id: p.player_id, name: p.name, handicap: p.handicap, teamKey: teamId, team_name: team.name, gross: 0, net: 0, points: 0, holeGross: [] });
          });

          holes.forEach(hole => {
            const holeScores = members.map(p => {
              const s = scores.find(s => s.player_id === p.player_id && s.hole === hole.hole);
              if (!s) return null;
              // Team Stableford/Medal/Aggregate all use the standard 95%
              // Team allowance (same HCP_95_FORMATS rule as Leaderboard.jsx/
              // IndividualScoreEntry.jsx), applied to Course HCP — not the old
              // event.handicap_pct multiplier, which that field no longer
              // means now it's been repurposed as HCP CAP.
              const hcp = Math.round(((p.hcp_adj ?? p.handicap) || 0) * 0.95);
              const base = Math.floor(hcp / 18);
              const extra = hcp % 18;
              const shots = base + (hole.stroke_index <= extra ? 1 : 0);
              const g = parseInt(s.gross);
              const net = g - shots;
              return { player_id: p.player_id, isGhost: p.isGhost, g, net, pts: Math.max(0, 2 + (hole.par - net)) };
            }).filter(s => s !== null);

            const sorted = [...holeScores].sort((a, b) => isMedal ? a.net - b.net : b.pts - a.pts);
            if (sorted.length > 0) {
              total += isAggregate ? sorted.reduce((s, v) => s + (isMedal ? v.net : v.pts), 0) : (isMedal ? sorted[0].net : sorted[0].pts);
              thru++;
            }

            // Each player's OWN individual gross/net/points — never a
            // teammate's. Applies whether this format is best-of-team or
            // aggregate; only the live team total (above) differs.
            members.forEach(p => {
              if (p.isGhost) return;
              const rec = archiveList.find(r => r.player_id === p.player_id && r.teamKey === teamId);
              const own = holeScores.find(c => c.player_id === p.player_id);
              rec.gross += own ? own.g : 0;
              rec.net += own ? own.net : 0;
              rec.points += own ? own.pts : 0;
              rec.holeGross.push(own ? own.g : 0);
            });
          });

          teamResults.push({
            teamId,
            name: team.name,
            members: members.map(p => p.name.split(" ")[0]).join(", "),
            total: thru > 0 ? total : "-",
            thru
          });
        });
      }

      teamResults.sort((a, b) => {
        if (isMedal) return (a.total === "-" ? 9999 : a.total) - (b.total === "-" ? 9999 : b.total);
        return (b.total === "-" ? -1 : b.total) - (a.total === "-" ? -1 : a.total);
      });
      teamResults.forEach((p, i) => { p.pos = i + 1; });
      setResults(teamResults);

      const posByTeamKey = {};
      teamResults.forEach(t => { posByTeamKey[t.teamId] = t.pos; });
      archiveList.forEach(r => { r.pos = posByTeamKey[r.teamKey] || 0; });
      setArchiveRows(archiveList);

      prizesRef.current = {
        nearest_pin: prizesRes.data.nearest_pin || "",
        nearest_pin2: prizesRes.data.nearest_pin2 || "",
        longest_drive: prizesRes.data.longest_drive || "",
      };
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const archiveResults = async () => {
    if (!event || archiveRows.length === 0) return;
    if (!window.confirm("Archive these Team results? This locks in the round permanently.")) return;
    setArchiving(true);
    try {
      const prizes = prizesRef.current;
      const archiveData = archiveRows.map(p => ({
        position: p.pos, rank: p.pos, player_name: p.name, handicap: p.handicap, team_name: p.team_name,
        gross: p.gross, net: p.net, points: p.points,
        holes: (p.holeGross || []).map((g, i) => ({ hole: i + 1, gross: g })).filter(hh => hh.gross > 0),
      }));
      const twos = [];
      archiveRows.forEach(p => { (p.holeGross || []).forEach((g, i) => { if (g === 2) twos.push({ player_name: p.name, hole: i + 1 }); }); });
      const res = await api.post("/archive/save", { event_id: event.id, results: archiveData, ...prizes, twos });
      showToast("Results archived!");
      const changes = res.data?.handicapChanges;
      if (changes && changes.length) {
        const lines = changes.map(c => `${c.name}: ${c.from} → ${c.to}`).join("\n");
        window.alert("Handicaps adjusted:\n\n" + lines);
      }
    } catch(e) { showToast("Failed: " + (e.response?.data?.error || e.message)); }
    finally { setArchiving(false); }
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");

  return (
    <Layout>
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ backgroundColor: "white", borderBottom: "1px solid #d1d5db", padding: "8px 16px", display: "flex", alignItems: "center", gap: "16px" }}>
          <span style={{ fontWeight: "600", fontSize: "14px" }}>Team Results</span>
          {event && <span style={{ fontSize: "12px", color: "#2563eb", fontWeight: "500" }}>{event.game_type}</span>}
          <button onClick={archiveResults} disabled={archiving || archiveRows.length === 0}
            style={{ marginLeft: "auto", backgroundColor: archiving || archiveRows.length === 0 ? "#d1d5db" : "#16a34a", color: "white", border: "none", padding: "6px 16px", borderRadius: "6px", fontWeight: "600", fontSize: "12px", cursor: archiving || archiveRows.length === 0 ? "not-allowed" : "pointer" }}>
            {archiving ? "Archiving..." : "Archive Results"}
          </button>
        </div>
        {toast && <div style={{ backgroundColor: "#eff6ff", color: "#1d4ed8", padding: "8px 16px", fontSize: "13px", borderBottom: "1px solid #bfdbfe" }}>{toast}</div>}
        <div style={{ flex: 1, overflowY: "auto" }}>
          <table style={{ width: "100%", fontSize: "14px", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, backgroundColor: "#14532d", color: "white" }}>
              <tr>
                <th style={{ padding: "8px 16px", textAlign: "center", width: "48px" }}>Pos</th>
                <th style={{ padding: "8px 16px", textAlign: "left" }}>Team</th>
                <th style={{ padding: "8px 16px", textAlign: "left" }}>Players</th>
                <th style={{ padding: "8px 16px", textAlign: "center", width: "64px" }}>Thru</th>
                <th style={{ padding: "8px 16px", textAlign: "center", width: "80px" }}>{isMedal ? "Net" : "Pts"}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={r.teamId} style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: i % 2 === 0 ? "white" : "#f0fdf4" }}>
                  <td style={{ padding: "8px 16px", textAlign: "center", fontWeight: "700" }}>{r.pos}</td>
                  <td style={{ padding: "8px 16px", fontWeight: "700", color: "#14532d" }}>{r.name}</td>
                  <td style={{ padding: "8px 16px", fontSize: "12px", color: "#6b7280" }}>{r.members}</td>
                  <td style={{ padding: "8px 16px", textAlign: "center" }}>{r.thru}</td>
                  <td style={{ padding: "8px 16px", textAlign: "center", fontWeight: "700", color: "#2563eb" }}>{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && results.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px", color: "#9ca3af" }}>No scores entered yet.</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
